import moment from "moment";
import { Subtitle } from "./subtitle";
import { SecEventType } from "./sec-events";
import { OriginMetadata } from "./origin-metadata";

export enum EShowOriginEnum {
  MAM = 1,
  DynamicScript,
  Block,
  Virtual,
}
export class ShowMetaData {
  constructor(
    public showOrigin: EShowOriginEnum,
    public originMetadata: OriginMetadata,
    public comment: string,
    public houseId: string,
    public reconcileKey: string,
    public missingSubsToChecks: string[],
  ) {}

  public toCreateMediaShowRequest(): any {
    return {
      ShowOrigin: this.showOrigin,
      Comment: this.comment,
      HouseId: this.houseId,
      ReconcileKey: this.reconcileKey,
      MissingSubsToChecks: this.missingSubsToChecks?.join("|"),
      Origin: this.originMetadata?.origin,
      OriginStartTime: this.originMetadata?.OriginStartTime,
    };
  }
}
export abstract class Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public som: moment.Duration,
    public id: string,
    public subtitles: Subtitle[] = [],
    public mediaDuration: moment.Duration,
    public mediaStartTimestampMs: number,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
    public order?: number,
    public secEvents?: Array<{
      id: string;
      type: SecEventType;
      textOverrides?: Array<{ Label: string; Text: string }>;
      properties: { [property: string]: any };
    }>,
    public cssStyles?: {},
    public OriginMetadata?: OriginMetadata,
    public comment?: string,
  ) {}

  public get endTime(): moment.Moment {
    return this.startTime
      .clone()
      .add(this.duration.asMilliseconds(), "milliseconds");
  }

  abstract getDisplayName(): string;

  abstract clone(): Show;
}

export class MediaShow extends Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public mediaId: number,
    public customId: string,
    public som: moment.Duration,
    public subtitles: Subtitle[] = [],
    public isContract: boolean = false,
    public mediaDuration: moment.Duration,
    public mediaStartTimestampMs: number,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      som,
      id,
      subtitles,
      mediaDuration,
      mediaStartTimestampMs,
      isAnchored,
      origin,
    );
  }

  public getDisplayName() {
    return this.name + " - " + this.customId;
  }

  clone(): MediaShow {
    return new MediaShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.mediaId,
      this.customId,
      this.som,
      this.subtitles,
      this.isContract,
      this.mediaDuration,
      this.mediaStartTimestampMs,
      this.isAnchored,
      this.origin,
    );
  }
}

export class LayoutShow extends Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public layoutUid: string,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      moment.duration(),
      id,
      null,
      null,
      0,
      isAnchored,
      origin,
    );
  }

  public getDisplayName() {
    return this.name;
  }

  clone(): Show {
    return new LayoutShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.layoutUid,
      this.isAnchored,
      this.origin,
    );
  }
}
export class BroadcastPresetShow extends Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public broadcastPresetUid: string,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      moment.duration(),
      id,
      null,
      null,
      0,
      isAnchored,
      origin,
    );
  }

  public getDisplayName() {
    return this.name;
  }

  clone(): Show {
    return new BroadcastPresetShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.broadcastPresetUid,
      this.isAnchored,
      this.origin,
    );
  }
}

export class GraphicsShow extends Show {
  public getDisplayName() {
    return this.name;
  }

  clone(): Show {
    return new GraphicsShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.graphicsUid,
    );
  }

  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public graphicsUid: string,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      moment.duration(),
      id,
      null,
      null,
      0,
      false,
      EShowOriginEnum.MAM,
    );
  }
}

export class LiveShow extends Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public feedId: string,
    public latencyMs: number,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      moment.duration(),
      id,
      null,
      null,
      0,
      isAnchored,
      origin,
    );
  }

  public getDisplayName() {
    return this.name;
  }

  clone(): Show {
    return new LiveShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.feedId,
      this.latencyMs,
      this.isAnchored,
      this.origin,
    );
  }
}

export class DynamicPromoShow extends Show {
  constructor(
    public name: string,
    public channelId: number,
    public thumbnailUrl: string,
    public startTime: moment.Moment,
    public duration: moment.Duration,
    public id: string,
    public promoId: string,
    public mediaDuration: moment.Duration,
    public isAnchored: boolean,
    public origin: EShowOriginEnum,
  ) {
    super(
      name,
      channelId,
      thumbnailUrl,
      startTime,
      duration,
      moment.duration(),
      id,
      null,
      mediaDuration,
      0,
      isAnchored,
      origin,
    );
  }

  public getDisplayName() {
    return this.name;
  }

  clone(): Show {
    return new DynamicPromoShow(
      this.name,
      this.channelId,
      this.thumbnailUrl,
      this.startTime,
      this.duration,
      this.id,
      this.promoId,
      this.mediaDuration,
      this.isAnchored,
      this.origin,
    );
  }
}

/**
 * Identifies broadcast preset rows from the scheduler; use instead of raw instanceof when
 * objects may be plain copies (e.g. deserialization) and lose the class prototype.
 */
export function isBroadcastPresetShowLike(show: unknown): boolean {
  if (show instanceof BroadcastPresetShow) {
    return true;
  }
  if (!show || typeof show !== "object") {
    return false;
  }
  const candidate = show as { broadcastPresetUid?: unknown };
  return (
    typeof candidate.broadcastPresetUid === "string" &&
    candidate.broadcastPresetUid.length > 0
  );
}
